import { ReportType } from "@edlab-fms/shared";
import { useMemo, useState } from "react";
import { useAuthQuery } from "../lib/api/auth";
import { useDonorQuery } from "../lib/api/donors";
import {
  buildReportFilters,
  useExportReportMutation,
  useReportPreviewQuery,
} from "../lib/api/reports";
import { formatMoney } from "../lib/format/money";
import { MonthSelector } from "../components/ui/MonthSelector";
import { useReportingPeriod } from "../lib/hooks/useReportingPeriod";
import { Button } from "../components/ui/Button";
import { Card } from "../components/ui/Card";
import { MetricCard } from "../components/ui/MetricCard";
import { PageHeader } from "../components/ui/PageHeader";
import { LoadingSpinner } from "../components/LoadingSpinner";
import { EmptyState } from "../components/ui/EmptyState";
import { ReportPreviewDisplay } from "../features/reports/ReportPreviewDisplay";

export function FundingPage() {
  const auth = useAuthQuery();
  const user = auth.data;
  const donorId = user?.donorId ?? null;
  const donor = useDonorQuery(donorId);
  const { period: month, setPeriod: setMonth, months } = useReportingPeriod();
  const [includeStudents, setIncludeStudents] = useState(false);

  const filters = useMemo(() => {
    if (!donorId) {
      return null;
    }
    return buildReportFilters({
      reportType: ReportType.DONOR,
      month,
      donorId,
      includeStudents,
    });
  }, [donorId, month, includeStudents]);

  const preview = useReportPreviewQuery(filters);
  const exportReport = useExportReportMutation();

  return (
    <div>
      <PageHeader
        eyebrow="My funding"
        title="Your support makes a difference."
        description={
          donor.data?.donor
            ? `${donor.data.donor.name} · Funding received, used, and remaining within your authorized view`
            : "Donor funding summary"
        }
        actions={
          donorId ? (
            <>
              <MonthSelector value={month} onChange={setMonth} months={months} />
              <Button
                variant="secondary"
                disabled={!filters}
                onClick={() => {
                  if (filters) {
                    void exportReport.mutateAsync({ format: "csv", filters });
                  }
                }}
              >
                Download update
              </Button>
              <Button
                disabled={!preview.data?.report}
                onClick={() => {
                  window.print();
                }}
              >
                Print / Save PDF
              </Button>
            </>
          ) : undefined
        }
      />

      {auth.isLoading || donor.isLoading ? (
        <LoadingSpinner label="Loading funding view…" className="py-16" />
      ) : !donorId ? (
        <EmptyState
          title="No donor profile linked"
          description="This account is not linked to a donor record yet. Contact your foundation administrator."
        />
      ) : donor.isError || !donor.data?.donor ? (
        <EmptyState
          title="Could not load your funding view"
          description="Your donor profile could not be loaded. Contact your foundation administrator."
        />
      ) : (
        <>
          <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
            <MetricCard
              variant="hero"
              label="Unspent funds"
              value={formatMoney(donor.data.donor.summary.unspent)}
              detail="Opening funds + received − paid"
            />
            <MetricCard
              label="Received"
              value={formatMoney(donor.data.donor.summary.received)}
              detail="Actually received to date"
            />
            <MetricCard
              label="Utilized"
              value={formatMoney(donor.data.donor.summary.utilized)}
              detail="Actual payments to date"
            />
            <MetricCard
              label="Outstanding pledge"
              value={formatMoney(donor.data.donor.summary.outstandingPledge)}
              detail="Pledged support still to be received"
            />
          </div>

          <Card className="mt-6" padding="md">
            <label className="flex items-center gap-2 text-sm text-edlab-ink">
              <input
                type="checkbox"
                checked={includeStudents}
                onChange={(event) => {
                  setIncludeStudents(event.target.checked);
                }}
              />
              Include supported students
            </label>
          </Card>

          {preview.isLoading ? (
            <LoadingSpinner label="Loading donor update…" className="py-16" />
          ) : preview.isError ? (
            <EmptyState
              title="Could not load donor update"
              description="Try again or contact your foundation administrator."
            />
          ) : preview.data?.report ? (
            <div className="mt-6">
              <ReportPreviewDisplay report={preview.data.report} />
            </div>
          ) : (
            <EmptyState
              title="No report data yet"
              description="Receipts and payments linked to your donor funds will appear here."
            />
          )}
        </>
      )}
    </div>
  );
}
